import { get, post, put, del, ApiError } from 'aws-amplify/api';

interface ResponsePayload<TResponse> {
    json(): Promise<TResponse>;
    text(): Promise<TResponse>;
}
/**
 * Basic response type of REST API.
 *
 * @internal
 */
export interface RestApiResponse<TResponse> {
    body: ResponsePayload<TResponse>;
    statusCode: number;
    headers: Headers;
}

const makeRequest = async <TResponse>(
    makeRequest: () => Promise<RestApiResponse<TResponse>>,
    method: string,
    options: Options,
): Promise<TResponse> => {
    try {
        const response = await makeRequest();
        // this may not contain a json body, handle gracefully if so
        try {
            const result = options.textResponse ? await response?.body?.text?.() : await response?.body?.json?.();
            return result as TResponse;
        } catch (e) {
            return null as any;
        }
    } catch (error) {
        if (error instanceof ApiError) {
            if (
                typeof error.response?.statusCode === 'number' &&
                options.ignoreErrorStatus?.includes(error.response?.statusCode)
            ) {
                return null as any;
            }

            console.error(JSON.stringify(error.response));
            console.error(
                JSON.stringify(
                    {
                        message: 'Error making request: ' + error.message,
                        status: error.response?.statusCode,
                        body: error.response?.body,
                        method,
                        correlationId: error.response?.headers?.['x-amzn-requestid'],
                    },
                    null,
                    2,
                ),
            );
        }

        throw error;
    }
};

type Options = {
    ignoreErrorStatus?: number[];
    textResponse?: boolean;
    retryStrategy?: {
        strategy: 'jittered-exponential-backoff' | 'no-retry';
    };
};

export type Init = {
    body?: any;
} & Options;

const getAPI = (apiName: string) => ({
    get: async <TResponse = any>(url: string, init: Init = {}) =>
        makeRequest<TResponse>(
            () => get({ apiName, path: url, options: init }).response as any as Promise<RestApiResponse<TResponse>>,
            'GET',
            init,
        ),
    post: async <TResponse = any>(url: string, init: Init = {}) =>
        makeRequest<TResponse>(
            () =>
                post({
                    apiName,
                    path: url,
                    options: {
                        body: init.body || {},
                        retryStrategy: init.retryStrategy,
                    },
                }).response as any as Promise<RestApiResponse<TResponse>>,
            'POST',
            init,
        ),
    del: async <TResponse = any>(url: string, init: Init = {}) =>
        makeRequest<TResponse>(
            () =>
                del({
                    apiName,
                    path: url,
                    options: {
                        retryStrategy: init.retryStrategy,
                    },
                }).response as any as Promise<RestApiResponse<TResponse>>,
            'DELETE',
            init,
        ),
    put: async <TResponse = any>(url: string, init: Init = {}) =>
        makeRequest<TResponse>(
            () =>
                put({
                    apiName,
                    path: url,
                    options: {
                        body: init.body || {},
                        retryStrategy: init.retryStrategy,
                    },
                }).response as any as Promise<RestApiResponse<TResponse>>,
            'PUT',
            init,
        ),
});

export const appAPI = getAPI('appApi');
export const publicAPI = getAPI('publicApi');
